import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileSignature, FileText, Mail, Plus, Trash2, Upload, Ban } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "../../api/client";
import * as contrattiApi from "../../api/contratti";
import { Contratto, DettaglioContratto, PuntoFirma, StatoContratto, TipoSchedaEconomica } from "../../api/contratti";
import * as filesApi from "../../api/files";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge, BadgeColor } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { RigheScheda } from "./RigheScheda";
import { LinkIndietro } from "../../components/ui/LinkIndietro";
import { useOrdinamentoUrl, useScriviParametri, useTornaIndietro } from "../../hooks/useStatoUrl";

// ============================================================================
// CONTRATTI DI CASH MANAGEMENT
//
// Il modulo è riservato ad admin e staff (decisione 2 del piano): centro
// assistenza, istituto di vigilanza e i loro operatori non lo vedono affatto,
// e la voce di menu non compare nemmeno — il permesso `contratti:read` è già
// il filtro, qui non si ricontrolla il ruolo.
//
// LA GENERAZIONE DEL PDF NON È ANCORA ATTIVA. Non è un errore da nascondere:
// la scheda lo dice apertamente e non mostra affatto il pulsante di invio,
// perché un bottone che fallisce senza spiegare è peggio di un bottone assente.
// ============================================================================

const COLORE_STATO: Record<StatoContratto, BadgeColor> = {
  bozza: "neutral",
  inviato_al_cliente: "primary",
  firmato: "success",
  annullato: "danger",
  // Storico, non attivo: si distingue da `firmato` anche a colpo d'occhio.
  archiviato: "neutral",
};

function dataIT(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

// ============================================================================
// Modal — nuovo contratto
// ============================================================================
function NuovoContrattoModal({
  open,
  onClose,
  onCreato,
}: {
  open: boolean;
  onClose: () => void;
  onCreato: (id: string) => void;
}) {
  const { showToast } = useToast();
  const [clienteNome, setClienteNome] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [riferimento, setRiferimento] = useState("");
  const [ordineVendita, setOrdineVendita] = useState("");
  const [dataContratto, setDataContratto] = useState("");
  const [schede, setSchede] = useState<TipoSchedaEconomica[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClienteNome("");
    setClienteId("");
    setRiferimento("");
    setOrdineVendita("");
    setDataContratto("");
    setSchede([]);
  }, [open]);

  function togglScheda(t: TipoSchedaEconomica) {
    setSchede((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  }

  async function salva() {
    setIsSaving(true);
    try {
      const { contratto } = await contrattiApi.generaContratto({
        cliente_id: clienteId,
        riferimento_contratto: riferimento || undefined,
        ordine_vendita: ordineVendita || undefined,
        data_contratto: dataContratto || undefined,
        schede,
      });
      showToast("Contratto creato in bozza.", "success");
      onClose();
      onCreato(contratto.id);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile creare il contratto."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nuovo contratto di Cash Management"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="button" loading={isSaving} disabled={!clienteId} onClick={salva}>
            Crea bozza
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ClienteAutocomplete
          label="Cliente"
          value={clienteNome}
          onChange={(v) => {
            setClienteNome(v);
            // Il testo cambiato a mano scollega la scelta precedente: senza,
            // resterebbe agganciato un cliente che non è più quello scritto.
            setClienteId("");
          }}
          onSelect={(c) => {
            setClienteId(c.id);
            setClienteNome(c.nome);
          }}
        />

        {/* Il contratto NON si intesta a un punto vendita: quello di Luce Srl
            ne copre due. I punti vendita stanno nelle righe delle schede, che
            arrivano quando l'elenco dei tipi sarà chiuso. */}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Riferimento contratto"
            value={riferimento}
            onChange={(e) => setRiferimento(e.target.value)}
            placeholder="es. EW0041"
          />
          <Input
            label="Ordine di vendita"
            value={ordineVendita}
            onChange={(e) => setOrdineVendita(e.target.value)}
            placeholder="es. S00632"
          />
        </div>

        <Input
          label="Data del contratto"
          type="date"
          value={dataContratto}
          onChange={(e) => setDataContratto(e.target.value)}
        />

        {/* --- Schede economiche ------------------------------------------
            Una per ogni servizio effettivamente venduto: si spuntano quelle
            che vanno allegate, e se ne possono aggiungere altre finché il
            contratto è in bozza. Non sono tre allegati fissi. */}
        <fieldset className="rounded-lg border border-line p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-ink/50">
            Schede economiche da allegare
          </legend>
          <div className="flex flex-col gap-2">
            {(Object.keys(contrattiApi.TIPO_SCHEDA_LABEL) as TipoSchedaEconomica[]).map((t) => (
              <label key={t} className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={schede.includes(t)}
                  onChange={() => togglScheda(t)}
                  className="h-4 w-4 rounded border-line text-primary"
                />
                <span className="text-sm text-ink">{contrattiApi.TIPO_SCHEDA_LABEL[t]}</span>
              </label>
            ))}
            <p className="text-xs text-ink/50">
              Solo i servizi venduti a questo cliente. Ogni scheda porta con sé la firma del Cliente che le
              spetta, e se ne possono aggiungere altre finché il contratto è in bozza.
            </p>
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}

// ============================================================================
// Scheda del contratto
// ============================================================================
function SchedaContratto({
  id,
  onIndietro,
  onCambiato,
}: {
  id: string;
  /** `replace` quando la scheda non esiste più (contratto eliminato). */
  onIndietro: (opzioni?: { replace?: boolean }) => void;
  onCambiato: () => void;
}) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [dati, setDati] = useState<DettaglioContratto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [inCorso, setInCorso] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const carica = useCallback(async () => {
    setIsLoading(true);
    try {
      setDati(await contrattiApi.getContratto(id));
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il contratto."), "error");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    carica();
  }, [carica]);

  if (isLoading) return <p className="py-12 text-center text-sm text-ink/50">Caricamento…</p>;
  // Come Dispositivi e Clienti: anche la scheda di un contratto che non c'è
  // più (eliminato, e riaperto con Avanti) offre la via per l'elenco.
  if (!dati)
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-ink/60">Contratto non trovato.</p>
        <LinkIndietro to="/contratti" className="mt-2 inline-block text-sm font-semibold text-primary hover:underline">
          Torna ai contratti
        </LinkIndietro>
      </div>
    );

  const { contratto, schede, punti_firma, firmato_ovunque } = dati;
  const puoScrivere = hasPermission("contratti:update");

  /**
   * Genera il PDF, o lo rifà.
   *
   * `carica()` dopo, non un aggiornamento a mano dello stato locale: la rotta
   * torna il contratto aggiornato ma cambia anche `template_versione`, e
   * rileggerlo e' l'unico modo di non tenersi una copia parziale.
   */
  async function generaPdf() {
    setInCorso("genera-pdf");
    try {
      await contrattiApi.generaPdfContratto(contratto.id);
      await carica();
      onCambiato();
      showToast("Documento generato.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Generazione del documento non riuscita."), "error");
    } finally {
      setInCorso(null);
    }
  }
  const modificabile = contratto.stato === "bozza";

  async function spunta(p: PuntoFirma, valore: boolean) {
    setInCorso(p.id);
    try {
      await contrattiApi.segnaFirma(p.id, valore);
      await carica();
      onCambiato();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiornare la firma."), "error");
    } finally {
      setInCorso(null);
    }
  }

  async function azione(nome: string, fn: () => Promise<unknown>, messaggio: string) {
    setInCorso(nome);
    try {
      await fn();
      await carica();
      onCambiato();
      showToast(messaggio, "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Operazione non riuscita."), "error");
    } finally {
      setInCorso(null);
    }
  }

  return (
    <div>
      <LinkIndietro to="/contratti" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink/60 hover:text-ink">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Tutti i contratti
      </LinkIndietro>

      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-card bg-primary/10 text-primary">
            <FileSignature className="h-6 w-6" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">{contratto.cliente?.nome ?? "Contratto"}</h1>
            <p className="text-sm text-ink/50">
              {contratto.riferimento_contratto ? `Rif. ${contratto.riferimento_contratto}` : "Senza riferimento"}
              {contratto.ordine_vendita ? ` · Ordine ${contratto.ordine_vendita}` : ""}
              {contratto.data_contratto ? ` · del ${dataIT(contratto.data_contratto)}` : ""}
            </p>
            <p className="mt-1 flex items-center gap-2">
              <Badge color={COLORE_STATO[contratto.stato]}>{contrattiApi.STATO_LABEL[contratto.stato]}</Badge>
              <span className="text-xs text-ink/45">generato il {dataIT(contratto.data_generazione)}</span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {puoScrivere && contratto.stato !== "annullato" && contratto.stato !== "firmato" && (
            <Button
              variant="ghost"
              loading={inCorso === "annulla"}
              onClick={() => {
                if (!window.confirm("Annullare questo contratto? Resta in elenco, ma esce dal flusso.")) return;
                azione("annulla", () => contrattiApi.annullaContratto(contratto.id), "Contratto annullato.");
              }}
            >
              <Ban className="h-4 w-4" aria-hidden="true" />
              Annulla
            </Button>
          )}
          {hasPermission("contratti:delete") && (
            <Button
              variant="ghost"
              loading={inCorso === "elimina"}
              onClick={() => {
                if (!window.confirm("Eliminare definitivamente questo contratto, i suoi PDF e le sue firme?")) return;
                setInCorso("elimina");
                contrattiApi
                  .eliminaContratto(contratto.id)
                  .then(() => {
                    showToast("Contratto eliminato.", "success");
                    onCambiato();
                    onIndietro({ replace: true });
                  })
                  .catch((err) => showToast(getErrorMessage(err, "Impossibile eliminare."), "error"))
                  .finally(() => setInCorso(null));
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Elimina
            </Button>
          )}
        </div>
      </header>

      {/* --- Il documento --------------------------------------------------
          Quando il PDF non c'è si dice PERCHÉ e si spegne l'invio: un bottone
          che fallisce senza spiegare manderebbe qualcuno a cercare un guasto
          che non esiste. */}
      <section className="mb-6 rounded-card border border-line bg-white p-5 shadow-card">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Documento</h2>

        {contratto.file_url_pdf ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                filesApi
                  .downloadProtectedFile(contratto.file_url_pdf!, `contratto-${contratto.id.slice(0, 8)}.pdf`)
                  .catch((err) => showToast(getErrorMessage(err, "Download non riuscito."), "error"))
              }
            >
              Scarica il contratto generato
            </Button>
            {/* Rigenerare ha senso solo in bozza, ed e' il motivo per cui la
                generazione e' manuale: dopo aver aggiunto una scheda economica
                il documento va rifatto. Oltre la bozza il backend risponde 409,
                quindi il pulsante sparisce invece di fallire. */}
            {puoScrivere && contratto.stato === "bozza" && (
              <Button
                variant="secondary"
                size="sm"
                loading={inCorso === "genera-pdf"}
                onClick={generaPdf}
                title="Rifà il documento con i dati e le schede di adesso"
              >
                Rigenera
              </Button>
            )}
            {/* Due canali, e il secondo dipende da un dato che il contratto
                può non avere: senza ordine di vendita Odoo non saprebbe a
                quale ordine allegare, quindi il pulsante è spento e lo dice
                invece di far fallire la chiamata. */}
            {puoScrivere && contratto.stato !== "annullato" && (
              <>
                <Button
                  size="sm"
                  loading={inCorso === "invia"}
                  onClick={() =>
                    azione(
                      "invia",
                      () => contrattiApi.inviaContratto(contratto.id, { canale: "email" }),
                      "Contratto spedito al Cliente."
                    )
                  }
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  Spedisci per email
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={inCorso === "invia-odoo"}
                  disabled={!contratto.ordine_vendita}
                  title={
                    contratto.ordine_vendita
                      ? `Allega il PDF all'ordine ${contratto.ordine_vendita} su Odoo e lascia spedire a Odoo`
                      : "Serve l'ordine di vendita: senza, Odoo non sa a quale ordine allegare il PDF"
                  }
                  onClick={async () => {
                    setInCorso("invia-odoo");
                    try {
                      const esito = await contrattiApi.inviaContratto(contratto.id, { canale: "odoo" });
                      await carica();
                      onCambiato();
                      // Odoo può aver solo allegato senza spedire: il messaggio
                      // che torna dice quale dei due, e va riportato tale e
                      // quale invece di dire sempre "spedito".
                      showToast(esito.messaggio ?? "Fatto.", esito.spedito ? "success" : "info");
                    } catch (err) {
                      showToast(getErrorMessage(err, "Invio via Odoo non riuscito."), "error");
                    } finally {
                      setInCorso(null);
                    }
                  }}
                >
                  Allega e spedisci da Odoo
                </Button>
              </>
            )}
          </div>
        ) : contratto.stato === "bozza" ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-ink/60">
              Il documento non è ancora stato generato. Senza, il contratto non si può né scaricare
              né spedire.
            </p>
            {puoScrivere && (
              <div>
                <Button size="sm" loading={inCorso === "genera-pdf"} onClick={generaPdf}>
                  <FileText className="h-4 w-4" aria-hidden="true" />
                  Genera il PDF
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Fuori dalla bozza il backend RIFIUTA di generare, ed è la scelta
             giusta: un documento rifatto dopo la spedizione sarebbe diverso da
             quello che il Cliente ha in mano, con lo stesso nome. Qui non si
             mostra un pulsante che verrebbe respinto con un 409 — si dice
             perché non c'è e cosa resta da fare. */
          <p className="rounded-lg border border-dashed border-accent/40 bg-accent/5 px-4 py-3 text-sm text-ink/70">
            Questo contratto non ha un documento generato e non è più una bozza, quindi non si può
            più generare: rifarlo adesso produrrebbe un file diverso da quello che il Cliente
            potrebbe già avere. Il contratto firmato di ritorno si archivia comunque qui sotto.
          </p>
        )}

        {contratto.data_invio && (
          <p className="mt-2 text-xs text-ink/45">Spedito al Cliente il {dataIT(contratto.data_invio)}</p>
        )}

        <div className="mt-4 border-t border-line pt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink/45">
            Contratto firmato di ritorno
          </p>
          {contratto.file_url_pdf_firmato ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                filesApi
                  .downloadProtectedFile(
                    contratto.file_url_pdf_firmato!,
                    `contratto-firmato-${contratto.id.slice(0, 8)}.pdf`
                  )
                  .catch((err) => showToast(getErrorMessage(err, "Download non riuscito."), "error"))
              }
            >
              Scarica il firmato
            </Button>
          ) : (
            <p className="text-sm text-ink/45">Non ancora caricato.</p>
          )}

          {puoScrivere && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  azione(
                    "upload",
                    () => contrattiApi.caricaContrattoFirmato(contratto.id, f),
                    "Documento firmato archiviato."
                  ).finally(() => {
                    // Azzerato sempre: senza, riselezionare lo STESSO file non
                    // farebbe scattare onChange.
                    if (fileRef.current) fileRef.current.value = "";
                  });
                }}
              />
              <Button
                className="mt-2"
                variant="ghost"
                size="sm"
                loading={inCorso === "upload"}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                {contratto.file_url_pdf_firmato ? "Sostituisci il firmato" : "Carica il firmato"}
              </Button>
              <p className="mt-1 text-xs text-ink/45">
                Il Cliente firma fuori dall&apos;app e rimanda il PDF: qui si archivia soltanto. Caricarlo non basta
                a dichiarare il contratto firmato — per quello ci sono le caselle qui sotto.
              </p>
            </>
          )}
        </div>
      </section>

      {/* --- Schede economiche ----------------------------------------------
          N per contratto, una per servizio venduto — non tre allegati fissi.
          Dal 27/08/2026 ogni scheda mostra anche le sue RIGHE (punti vendita,
          canoni, apparati, voci di manutenzione) e le fa compilare: prima
          esistevano a schema e finivano nel PDF, ma si scrivevano solo da SQL. */}
      <section className="mb-6 rounded-card border border-line bg-white p-5 shadow-card">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">Schede economiche allegate</h2>
          <span className="text-xs text-ink/45">{schede.length}</span>
        </div>

        {schede.length === 0 ? (
          <p className="rounded-lg border border-dashed border-line px-4 py-4 text-center text-sm text-ink/45">
            Nessuna scheda allegata: il contratto non ha ancora un oggetto.
          </p>
        ) : (
          <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
            {schede.map((sc) => (
              <li key={sc.id} className="px-3 py-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">
                      {sc.etichetta ?? contrattiApi.TIPO_SCHEDA_LABEL[sc.tipo]}
                    </p>
                    {sc.note && <p className="mt-0.5 text-xs text-ink/45">{sc.note}</p>}
                  </div>
                  {puoScrivere && modificabile && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      loading={inCorso === sc.id}
                      onClick={() => {
                        if (!window.confirm("Togliere questa scheda dal contratto? Sparisce anche la sua firma.")) return;
                        azione(sc.id, () => contrattiApi.rimuoviScheda(sc.id), "Scheda rimossa.");
                      }}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      Togli
                    </Button>
                  )}
                </div>
                {/* Le righe si vedono SEMPRE, anche a contratto spedito: quello
                    che il Cliente ha in mano si deve poter rileggere. È la
                    modifica che si spegne, non la lettura. */}
                {/* `carica` e non `onCambiato`: quello ricarica l'ELENCO dei
                    contratti, e le righe stanno nel DETTAGLIO. L'elenco non
                    mostra nulla che una riga cambi, quindi non serve toccarlo. */}
                <RigheScheda scheda={sc} modificabile={puoScrivere && modificabile} onCambiato={carica} />
              </li>
            ))}
          </ul>
        )}

        {puoScrivere && modificabile && (
          <div className="mt-3 flex flex-wrap gap-2">
            {(Object.keys(contrattiApi.TIPO_SCHEDA_LABEL) as TipoSchedaEconomica[])
              .filter((t) => !schede.some((sc) => sc.tipo === t))
              .map((t) => (
                <Button
                  key={t}
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={inCorso === t}
                  onClick={() =>
                    azione(t, () => contrattiApi.aggiungiScheda(contratto.id, t), "Scheda allegata.")
                  }
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {contrattiApi.TIPO_SCHEDA_LABEL[t]}
                </Button>
              ))}
            <p className="w-full text-xs text-ink/45">
              Il contratto prevede che se ne aggiungano anche dopo, come allegati integrativi. Ogni scheda porta con
              sé la firma del Cliente che le spetta.
            </p>
          </div>
        )}
      </section>

      {/* --- Firme ---------------------------------------------------------
          Nove caselle, non un flag unico: la domanda operativa non è "è
          firmato?" ma "è firmato DOVE" — il Cliente può rimandare il corpo e
          dimenticare l'Allegato 2, ed è esattamente il caso da vedere. */}
      <section className="rounded-card border border-line bg-white p-5 shadow-card">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">Punti di firma</h2>
          <span className="text-xs text-ink/45">
            {punti_firma.filter((p) => p.firmato).length} di {punti_firma.length}
            {firmato_ovunque ? " — firmato dappertutto" : ""}
          </span>
        </div>

        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
          {punti_firma.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
              <label className="flex flex-1 cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={p.firmato}
                  disabled={!puoScrivere || inCorso === p.id || contratto.stato === "annullato"}
                  onChange={(e) => spunta(p, e.target.checked)}
                  className="h-4 w-4 rounded border-line text-primary"
                />
                <span className="text-sm text-ink">{p.etichetta}</span>
              </label>
              <span className="text-xs text-ink/45">
                <Badge color={p.parte === "cliente" ? "neutral" : "primary"}>
                  {p.parte === "cliente" ? "Cliente" : "EVO"}
                </Badge>
                {p.firmato && p.data_firma ? <span className="ml-2">{dataIT(p.data_firma)}</span> : null}
              </span>
            </li>
          ))}
        </ul>

        {puoScrivere && (
          <p className="mt-2 text-xs text-ink/45">
            Si spunta dopo aver <strong>guardato</strong> il PDF firmato: la casella registra chi ha verificato.
            Quando tutte e {punti_firma.length} sono spuntate il contratto passa a «Firmato».
          </p>
        )}
      </section>
    </div>
  );
}

// ============================================================================
// Pagina
// ============================================================================
export default function Contratti() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [contratti, setContratti] = useState<Contratto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // La scheda di un contratto è una pagina intera e ha la sua rotta,
  // `/contratti/:id`, come `/clients/:id` e `/devices/:id`: Indietro dalla
  // scheda torna all'elenco, filtrato e ordinato com'era (`?stato=`,
  // `?ordina=`), e la scheda si può aprire da un link.
  const { id: selezionato } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const torna = useTornaIndietro();
  const [searchParams] = useSearchParams();
  const scrivi = useScriviParametri();
  const { ordine, onOrdineChange } = useOrdinamentoUrl();
  const statoUrl = searchParams.get("stato") ?? "";
  const stato: StatoContratto | "" = Object.keys(contrattiApi.STATO_LABEL).includes(statoUrl)
    ? (statoUrl as StatoContratto)
    : "";
  const [nuovoOpen, setNuovoOpen] = useState(false);

  const carica = useCallback(async () => {
    setIsLoading(true);
    try {
      const { contratti } = await contrattiApi.listContratti(stato ? { stato } : {});
      setContratti(contratti);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare i contratti."), "error");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stato]);

  useEffect(() => {
    carica();
  }, [carica]);

  if (selezionato) {
    return (
      <SchedaContratto
        id={selezionato}
        onIndietro={(opzioni) => torna("/contratti", opzioni)}
        onCambiato={carica}
      />
    );
  }

  const colonne: TableColumn<Contratto>[] = [
    {
      key: "cliente",
      header: "Cliente",
      sempreVisibile: true,
      sortValue: (c) => c.cliente?.nome ?? "",
      render: (c) => <span className="font-semibold text-ink">{c.cliente?.nome ?? "—"}</span>,
    },
    {
      key: "riferimento",
      header: "Riferimento",
      sortValue: (c) => c.riferimento_contratto ?? "",
      render: (c) => c.riferimento_contratto ?? "—",
    },
    {
      key: "ordine",
      header: "Ordine di vendita",
      sortValue: (c) => c.ordine_vendita ?? "",
      render: (c) => c.ordine_vendita ?? "—",
    },
    {
      key: "schede",
      header: "Schede",
      sortValue: (c) => c._count?.schede ?? 0,
      render: (c) => String(c._count?.schede ?? 0),
    },
    {
      key: "stato",
      header: "Stato",
      sortValue: (c) => c.stato,
      render: (c) => <Badge color={COLORE_STATO[c.stato]}>{contrattiApi.STATO_LABEL[c.stato]}</Badge>,
    },
    {
      key: "data",
      header: "Data contratto",
      sortValue: (c) => c.data_contratto ?? "",
      render: (c) => dataIT(c.data_contratto),
    },
  ];

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Contratti</h1>
          <p className="text-sm text-ink/50">Cash Management — Wink on Cash</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={stato}
            onChange={(e) => scrivi({ stato: e.target.value })}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            aria-label="Filtra per stato"
          >
            <option value="">Tutti gli stati</option>
            {(Object.keys(contrattiApi.STATO_LABEL) as StatoContratto[]).map((s) => (
              <option key={s} value={s}>
                {contrattiApi.STATO_LABEL[s]}
              </option>
            ))}
          </select>
          {hasPermission("contratti:create") && (
            <Button onClick={() => setNuovoOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuovo contratto
            </Button>
          )}
        </div>
      </header>

      <Table
        columns={colonne}
        ordinabile
        ordine={ordine}
        onOrdineChange={onOrdineChange}
        sceltaColonne="contratti"
        data={contratti}
        rowKey={(c) => c.id}
        isLoading={isLoading}
        emptyMessage="Nessun contratto registrato."
        onRowClick={(c) => navigate(`/contratti/${c.id}`)}
      />

      <NuovoContrattoModal
        open={nuovoOpen}
        onClose={() => setNuovoOpen(false)}
        onCreato={(id) => {
          carica();
          navigate(`/contratti/${id}`);
        }}
      />
    </div>
  );
}
